import type { ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCircleExclamation, FaArrowsRotate, FaHouse } from 'react-icons/fa6';
import { useNavigate } from 'react-router';
import './ErrorPage.scss';

interface ErrorPageProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetError?: () => void;
}

/**
 * ErrorPage - User-friendly error display component
 *
 * In development: Shows full error details including stack trace
 * In production: Shows generic error message without technical details
 *
 * @param {Error} error - The error object
 * @param {Object} errorInfo - React error info with component stack
 * @param {Function} resetError - Function to reset error boundary state
 */
const ErrorPage = ({ error, errorInfo, resetError }: ErrorPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDevelopment = process.env.NODE_ENV === 'development';

  const handleGoHome = () => {
    if (resetError) resetError();
    navigate('/');
  };

  const handleReload = () => {
    if (resetError) resetError();
    window.location.reload();
  };

  return (
    <div className="error-page">
      <div className="error-page-content">
        <div className="error-icon">
          <FaCircleExclamation size={64} />
        </div>

        <h1 className="error-title">{t('error.boundary.title')}</h1>

        <p className="error-message">
          {t('error.boundary.message')}
        </p>

        <div className="error-actions">
          <button
            className="button is-primary"
            onClick={handleReload}
          >
            <span className="icon">
              <FaArrowsRotate />
            </span>
            <span>{t('error.boundary.reload')}</span>
          </button>

          <button
            className="button"
            onClick={handleGoHome}
          >
            <span className="icon">
              <FaHouse />
            </span>
            <span>{t('error.boundary.go_home')}</span>
          </button>
        </div>

        {/* Show technical details only in development */}
        {isDevelopment && error && (
          <div className="error-details">
            <details>
              <summary className="error-details-summary">
                {t('error.boundary.technical_details')}
              </summary>

              <div className="error-details-content">
                <h3>{t('error.boundary.error_message')}</h3>
                <pre>{error.toString()}</pre>

                {error.stack && (
                  <>
                    <h3>{t('error.boundary.stack_trace')}</h3>
                    <pre>{error.stack}</pre>
                  </>
                )}

                {errorInfo && errorInfo.componentStack && (
                  <>
                    <h3>{t('error.boundary.component_stack')}</h3>
                    <pre>{errorInfo.componentStack}</pre>
                  </>
                )}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;
